import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import EventForm from "@/components/EventForm";
import EventsTable from "@/components/EventsTable";
import UpcomingEvents from "@/components/UpcomingEvents";
import AdSpaceSection from "@/components/AdSpaceSection";
import StoriesReel from "@/components/StoriesReel";
import SocialLinks from "@/components/SocialLinks";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CalendarDays, Plus, Sparkles } from "lucide-react";

const MemberPanel = () => {
  const { settings } = useSettings();
  const [approved, setApproved] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [approvedRes, allRes] = await Promise.all([
        api.get("/events/approved"),
        api.get("/events"),
      ]);
      setApproved(approvedRes.data);
      const sorted = [...allRes.data].sort((a, b) =>
        (b.created_at || "").localeCompare(a.created_at || "")
      );
      setRecent(sorted.slice(0, 1));
    } catch (err) {
      toast.error("Etkinlikler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // All approved events sorted by event_datetime ascending (upcoming first)
  const allByDate = useMemo(() => {
    return [...approved].sort(
      (a, b) => new Date(a.event_datetime) - new Date(b.event_datetime)
    );
  }, [approved]);

  useEffect(() => { fetchAll(); }, []);

  return (
    <div className="space-y-8" data-testid="member-panel">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#8A252C] to-[#6f1d24] text-[#F9F6F0] p-8 sm:p-12 pattern-lines" data-testid="hero-banner">
        <div className="absolute top-0 right-0 w-64 h-64 -mt-20 -mr-20 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 -mb-20 -ml-20 rounded-full bg-[#D4AF37]/5 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-[11px] uppercase tracking-[0.2em] font-bold">
              <Sparkles className="w-3.5 h-3.5" />
              {settings.hero_kicker}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-4" data-testid="hero-title">
              {settings.hero_title}{" "}
              <span className="text-[#D4AF37]">{settings.hero_highlight}</span>
            </h1>
            <p className="mt-4 text-[#F9F6F0]/85 text-base sm:text-lg leading-relaxed max-w-xl" data-testid="hero-description">
              {settings.hero_description}
            </p>
          </div>
          <div className="shrink-0">
            <Button
              onClick={() => setFormOpen(true)}
              data-testid="open-form-btn"
              className="bg-[#D4AF37] hover:bg-[#c19e2f] text-[#1F2937] rounded-xl h-14 px-6 gap-2 text-base font-bold shadow-lg"
            >
              <Plus className="w-5 h-5" />
              Yeni Etkinlik Ekle
            </Button>
          </div>
        </div>
      </section>

      <StoriesReel />

      <UpcomingEvents events={approved} daysAhead={7} />

      <AdSpaceSection />

      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#8A252C]/10 flex items-center justify-center">
            <CalendarDays className="w-4 h-4 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Kayıtlar</div>
            <h2 className="font-display text-2xl font-bold text-[#1F2937]">Son Bildirilen Etkinlikler</h2>
          </div>
        </div>
        {loading ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-[#4B5563]">Yükleniyor...</div>
        ) : (
          <EventsTable events={recent} emptyText="Henüz kayıtlı etkinlik bulunmuyor. Yukarıdaki butona tıklayarak ilk siz bildirin." />
        )}
      </section>

      {/* All events sorted by upcoming date ascending */}
      <section className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
            <CalendarDays className="w-4 h-4 text-[#8A252C]" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A252C]/80 font-bold">Takvim</div>
            <h2 className="font-display text-2xl font-bold text-[#1F2937]">Tüm Etkinlikler</h2>
            <p className="text-xs text-[#4B5563]">Tarihi yaklaşan önce olacak şekilde küçükten büyüğe.</p>
          </div>
        </div>
        {loading ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-10 text-center text-[#4B5563]">Yükleniyor...</div>
        ) : (
          <EventsTable
            events={allByDate}
            emptyText="Henüz onaylı etkinlik yok."
          />
        )}
      </section>

      <SocialLinks />

      {/* New event form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent
          className="max-w-3xl bg-[#F9F6F0] border-stone-200 max-h-[92vh] overflow-y-auto"
          data-testid="new-event-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1F2937]">
              Yeni Etkinlik Bildir
            </DialogTitle>
            <p className="text-sm text-[#4B5563]">
              Formu doldurun, yönetici onayının ardından takvimde yer alacak.
            </p>
          </DialogHeader>
          <EventForm
            onCreated={() => {
              fetchAll();
              // Auto-close after success screen shows briefly
              setTimeout(() => setFormOpen(false), 1500);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberPanel;
